import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { ChessArrow } from "@/shared/components/chessboard";

type ExplainedArrow = ChessArrow;
const sameArrow = (left: ChessArrow, right: ChessArrow) =>
  left.startSquare === right.startSquare && left.endSquare === right.endSquare && left.color === right.color;

/** Observe the rendered SVG stroke; never put a pointer-catching layer over pieces. */
export function RelationshipArrowHover({
  arrows,
  fen,
  orientation,
}: {
  arrows: ExplainedArrow[];
  fen: string;
  orientation: "white" | "black";
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const latestArrows = useRef(arrows);
  const [hover, setHover] = useState<{
    arrow: ExplainedArrow;
    fen: string;
    orientation: string;
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    latestArrows.current = arrows;
    setHover((current) => (current && !arrows.some((arrow) => sameArrow(arrow, current.arrow)) ? null : current));
  }, [arrows]);
  useEffect(() => {
    const board = anchor.current?.parentElement;
    if (!board) return;
    let activePath: SVGPathElement | null = null;
    const clear = () => {
      activePath?.removeAttribute("data-relationship-hovered");
      activePath = null;
      setHover(null);
    };
    const move = (event: PointerEvent) => {
      if (
        event.pointerType !== "mouse" ||
        event.buttons ||
        (event.target instanceof Element && event.target.closest("[data-evidence-interactive]"))
      ) {
        clear();
        return;
      }
      for (const path of board.querySelectorAll<SVGPathElement>("path[marker-end]")) {
        const arrow = latestArrows.current.find(
          (candidate) =>
            path.getAttribute("marker-end")?.endsWith(`-${candidate.startSquare}-${candidate.endSquare})`) &&
            path.getAttribute("stroke") === candidate.color,
        );
        if (!arrow) continue;
        const matrix = path.getScreenCTM();
        if (
          !matrix ||
          !path.isPointInStroke(new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse()))
        )
          continue;
        if (activePath !== path) {
          activePath?.removeAttribute("data-relationship-hovered");
          activePath = path;
        }
        path.setAttribute("data-relationship-hovered", "true");
        const rect = board.getBoundingClientRect();
        setHover({
          arrow,
          fen,
          orientation,
          x: Math.max(4, Math.min(event.clientX - rect.left + 12, rect.width - 264)),
          y:
            event.clientY - rect.top < rect.height / 2
              ? event.clientY - rect.top + 16
              : Math.max(4, event.clientY - rect.top - 130),
        });
        return;
      }
      clear();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") clear();
    };
    clear();
    board.addEventListener("pointermove", move, true);
    board.addEventListener("pointerdown", clear, true);
    board.addEventListener("pointerleave", clear);
    document.addEventListener("keydown", key);
    return () => {
      activePath?.removeAttribute("data-relationship-hovered");
      board.removeEventListener("pointermove", move, true);
      board.removeEventListener("pointerdown", clear, true);
      board.removeEventListener("pointerleave", clear);
      document.removeEventListener("keydown", key);
    };
  }, [fen, orientation]);
  const currentArrow = hover && arrows.find((arrow) => sameArrow(arrow, hover.arrow));
  const current =
    hover?.fen === fen && hover.orientation === orientation && currentArrow ? { ...hover, arrow: currentArrow } : null;
  return (
    <>
      <span ref={anchor} hidden />
      {current && (
        <Box
          role="tooltip"
          data-testid="relationship-arrow-tooltip"
          style={{ left: current.x, top: current.y }}
          sx={{
            position: "absolute",
            zIndex: 70,
            pointerEvents: "none",
            width: 260,
            maxWidth: "calc(100% - 8px)",
            p: 1,
            bgcolor: "background.paper",
            color: "text.primary",
            border: "1px solid",
            borderColor: current.arrow.color,
            boxShadow: 3,
          }}
        >
          <Typography sx={{ fontSize: ".72rem", fontWeight: 900, mb: 0.5 }}>
            {current.arrow.label ?? "Board arrow"}
          </Typography>
          <Typography sx={{ fontSize: ".72rem", lineHeight: 1.4 }}>
            {current.arrow.description ?? `Arrow from ${current.arrow.startSquare} to ${current.arrow.endSquare}.`}
          </Typography>
        </Box>
      )}
    </>
  );
}
